/**
 * Utility functions for date formatting
 */

/**
 * Format date string to yyyy-MM-dd format for Strapi API
 * @param dateString - The date string to format
 * @returns Formatted date string in yyyy-MM-dd format or undefined
 */
export const formatDateForAPI = (dateString: string | undefined): string | undefined => {
  if (!dateString) return undefined;
  // If already in yyyy-MM-dd format, return as is
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) return dateString;
  // Convert to yyyy-MM-dd format
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return undefined;
  return date.toISOString().split('T')[0];
};

/**
 * Format date string for HTML date input (yyyy-MM-dd)
 * @param dateString - The date string to format
 * @returns Formatted date string in yyyy-MM-dd format or empty string
 */
export const formatDateForInput = (dateString: string | undefined): string => {
  if (!dateString) return '';
  // If already in yyyy-MM-dd format, return as is
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) return dateString;
  // Convert to yyyy-MM-dd format
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';
  return date.toISOString().split('T')[0];
};

/**
 * Format date string for HTML month input (yyyy-MM)
 * @param dateString - The date string to format
 * @returns Formatted string in yyyy-MM format or empty string
 */
export const formatMonthForInput = (dateString: string | undefined): string => {
  if (!dateString) return '';
  if (/^\d{4}-\d{2}$/.test(dateString)) return dateString;
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) return dateString.slice(0, 7);
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 7);
};

/**
 * Convert an HTML month input value (yyyy-MM) to a full date (yyyy-MM-01)
 * accepted by Strapi date fields.
 */
export const formatMonthForAPI = (monthString: string | undefined): string | undefined => {
  if (!monthString) return undefined;
  if (/^\d{4}-\d{2}$/.test(monthString)) return `${monthString}-01`;
  return formatDateForAPI(monthString);
};

/**
 * Format date object with all date fields for API submission
 * @param data - The data object containing date fields
 * @param dateFields - Array of field names that contain dates
 * @returns Data object with formatted dates
 */
export const formatDatesForAPI = <T extends Record<string, any>>(
  data: T,
  dateFields: (keyof T)[]
): T => {
  const formatted = { ...data };
  dateFields.forEach((field) => {
    if (formatted[field]) {
      formatted[field] = formatDateForAPI(formatted[field] as string) as any;
    }
  });
  return formatted;
};

